/**
 * VIP授权码管理页面
 * 提供授权码的生成、查询、管理和导出功能
 */

import { useState, useEffect, useCallback } from 'react';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { SiteHeader } from '@/components/SiteHeder';
import {
  getVipCodeStats,
  getVipCodeBatches,
  getVipCodeList,
  generateVipCodes,
  disableVipCode,
  deleteVipCode,
  exportVipCodes,
  getVipLevelDisplay,
  getCodeStatusDisplay,
} from '../services';
import type {
  VipActivationCode,
  VipCodeStats,
  VipCodeBatch,
  VipCodeStatus,
} from '../services';
import * as XLSX from 'xlsx';

export default function VipCodeManagementPage() {
  // 状态管理
  const [codes, setCodes] = useState<VipActivationCode[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [stats, setStats] = useState<VipCodeStats | null>(null);
  const [batches, setBatches] = useState<VipCodeBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBatchesModal, setShowBatchesModal] = useState(false);

  // 查询参数
  const [queryParams, setQueryParams] = useState({
    status: '' as VipCodeStatus | '',
    vipLevel: '' as string,
    batchId: '',
    keyword: '',
    page: 1,
    pageSize: 20,
  });

  // 生成授权码表单
  const [generateForm, setGenerateForm] = useState({
    name: '',
    vip_level: 1,
    duration_months: 12,
    count: 10,
    remark: '',
  });

  // 导出参数
  const [exportParams, setExportParams] = useState({
    status: '' as VipCodeStatus | '',
    vipLevel: '' as string,
    batchId: '',
  });

  // 加载授权码列表
  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {
        page: queryParams.page,
        pageSize: queryParams.pageSize,
      };
      if (queryParams.status) params.status = queryParams.status;
      if (queryParams.vipLevel) params.vipLevel = Number.parseInt(queryParams.vipLevel);
      if (queryParams.batchId) params.batchId = queryParams.batchId;
      if (queryParams.keyword) params.keyword = queryParams.keyword;

      const result = await getVipCodeList(params as Parameters<typeof getVipCodeList>[0]);
      if (result.success && result.data) {
        setCodes(result.data.data);
        setPagination(result.data.pagination);
      }
    } catch (error) {
      console.error('加载授权码列表失败:', error);
      alert(error instanceof Error ? error.message : '加载授权码列表失败');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  // 加载统计信息
  const loadStats = async () => {
    try {
      const result = await getVipCodeStats();
      if (result.success && result.data) {
        setStats(result.data.stats);
      }
    } catch (error) {
      console.error('加载统计信息失败:', error);
    }
  };

  // 加载批次列表
  const loadBatches = async () => {
    try {
      const result = await getVipCodeBatches();
      if (result.success && result.data) {
        setBatches(result.data.batches);
      }
    } catch (error) {
      console.error('加载批次列表失败:', error);
    }
  };

  // 初始加载
  useEffect(() => {
    loadCodes();
    loadStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCodes]);

  // 生成授权码
  const handleGenerate = async () => {
    if (!generateForm.name.trim()) {
      alert('请输入授权码名称');
      return;
    }

    try {
      const result = await generateVipCodes(generateForm);
      if (result.success && result.data) {
        alert(`成功生成 ${result.data.count} 个授权码`);
        setShowGenerateModal(false);
        setGenerateForm({
          name: '',
          vip_level: 1,
          duration_months: 12,
          count: 10,
          remark: '',
        });
        loadCodes();
        loadStats();
      }
    } catch (error) {
      console.error('生成授权码失败:', error);
      alert(error instanceof Error ? error.message : '生成授权码失败');
    }
  };

  // 禁用授权码
  const handleDisable = async (code: VipActivationCode) => {
    if (!confirm(`确定要禁用授权码 ${code.code} 吗？`)) {
      return;
    }

    try {
      const result = await disableVipCode(code.id);
      if (result.success) {
        alert('授权码已禁用');
        loadCodes();
        loadStats();
      }
    } catch (error) {
      console.error('禁用授权码失败:', error);
      alert(error instanceof Error ? error.message : '禁用授权码失败');
    }
  };

  // 删除授权码
  const handleDelete = async (code: VipActivationCode) => {
    if (!confirm(`确定要删除授权码 ${code.code} 吗？此操作不可恢复！`)) {
      return;
    }

    try {
      const result = await deleteVipCode(code.id);
      if (result.success) {
        alert('授权码已删除');
        loadCodes();
        loadStats();
      }
    } catch (error) {
      console.error('删除授权码失败:', error);
      alert(error instanceof Error ? error.message : '删除授权码失败');
    }
  };

  // 复制授权码
  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      alert('已复制到剪贴板');
    } catch {
      // 降级方案
      const input = document.createElement('input');
      input.value = code;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      alert('已复制到剪贴板');
    }
  };

  // 导出授权码为Excel
  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {};
      if (exportParams.status) params.status = exportParams.status;
      if (exportParams.vipLevel) params.vipLevel = Number.parseInt(exportParams.vipLevel);
      if (exportParams.batchId) params.batchId = exportParams.batchId;

      const result = await exportVipCodes(params as Parameters<typeof exportVipCodes>[0]);
      if (result.success && result.data && result.data.codes.length > 0) {
        // 创建工作簿
        const ws = XLSX.utils.json_to_sheet(result.data.codes);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'VIP授权码');

        // 设置列宽
        ws['!cols'] = [
          { wch: 20 }, // 授权码
          { wch: 25 }, // 名称
          { wch: 10 }, // VIP等级
          { wch: 12 }, // 有效期月数
          { wch: 10 }, // 状态
          { wch: 36 }, // 使用者ID
          { wch: 20 }, // 使用时间
          { wch: 20 }, // 创建时间
          { wch: 30 }, // 备注
        ];

        // 下载文件
        const fileName = `VIP授权码_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
        
        alert(`成功导出 ${result.data.count} 条授权码`);
        setShowExportModal(false);
      } else {
        alert('没有可导出的数据');
      }
    } catch (error) {
      console.error('导出授权码失败:', error);
      alert(error instanceof Error ? error.message : '导出授权码失败');
    }
  };

  // 分页切换
  const handlePageChange = (newPage: number) => {
    setQueryParams({ ...queryParams, page: newPage });
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <SidebarProvider>
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title="VIP授权码管理" />
        <div className="flex flex-1 flex-col p-4">
          <div className="container mx-auto">
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400">
                生成和管理VIP/VVIP会员授权码，支持批量生成和Excel导出
              </p>
            </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">总数</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">未使用</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {stats.unused_count}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">已使用</div>
            <div className="text-2xl font-bold text-gray-500 mt-1">
              {stats.used_count}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">已禁用</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              {stats.disabled_count}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">VIP码</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {stats.vip_count}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">VVIP码</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">
              {stats.vvip_count}
            </div>
          </div>
        </div>
      )}

      {/* 操作栏 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <button
            type="button"
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            生成授权码
          </button>
          <button
            type="button"
            onClick={() => {
              loadBatches();
              setShowBatchesModal(true);
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            批次管理
          </button>
          <button
            type="button"
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            导出Excel
          </button>

          {/* 筛选器 */}
          <div className="flex gap-2 items-center flex-wrap">
            <select
              value={queryParams.status}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  status: e.target.value as VipCodeStatus | '',
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">全部状态</option>
              <option value="unused">未使用</option>
              <option value="used">已使用</option>
              <option value="disabled">已禁用</option>
            </select>

            <select
              value={queryParams.vipLevel}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  vipLevel: e.target.value,
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">全部等级</option>
              <option value="1">VIP</option>
              <option value="2">VVIP</option>
            </select>

            <input
              type="text"
              placeholder="搜索授权码/名称"
              value={queryParams.keyword}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  keyword: e.target.value,
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48"
            />
          </div>
        </div>
      </div>

      {/* 授权码列表 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  授权码
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  等级
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  有效期
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  使用者
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  创建时间
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-center text-gray-500 dark:text-gray-400">
                    加载中...
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-center text-gray-500 dark:text-gray-400">
                    暂无数据
                  </td>
                </tr>
              ) : (
                codes.map((code) => {
                  const levelInfo = getVipLevelDisplay(code.vip_level);
                  const statusInfo = getCodeStatusDisplay(code.status);
                  return (
                    <tr key={code.id}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono font-medium text-gray-900 dark:text-white">
                            {code.code}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopy(code.code)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title="复制"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {code.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${levelInfo.bgColor} ${levelInfo.textColor}`}>
                          {levelInfo.name}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {code.duration_months}个月
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                          {statusInfo.name}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {code.used_by ? (
                          <span className="font-mono text-xs">{code.used_by.substring(0, 8)}...</span>
                        ) : '-'}
                        {code.used_at && (
                          <div className="text-xs text-gray-400">{formatDateTime(code.used_at)}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {formatDateTime(code.created_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {code.status === 'unused' && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleDisable(code)}
                              className="text-yellow-600 hover:text-yellow-700"
                            >
                              禁用
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(code)}
                              className="text-red-600 hover:text-red-700"
                            >
                              删除
                            </button>
                          </div>
                        )}
                        {code.status === 'disabled' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(code)}
                            className="text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {pagination.total_pages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.total_pages} 页
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 生成授权码弹窗 */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                生成授权码
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  名称 *
                </label>
                <input
                  type="text"
                  value={generateForm.name}
                  onChange={(e) => setGenerateForm({ ...generateForm, name: e.target.value })}
                  placeholder="如：VIP授权码-2026年1月"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  VIP等级 *
                </label>
                <select
                  value={generateForm.vip_level}
                  onChange={(e) => setGenerateForm({ ...generateForm, vip_level: Number.parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value={1}>VIP会员</option>
                  <option value={2}>VVIP会员</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  有效期（月）*
                </label>
                <select
                  value={generateForm.duration_months}
                  onChange={(e) => setGenerateForm({ ...generateForm, duration_months: Number.parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value={1}>1个月</option>
                  <option value={3}>3个月</option>
                  <option value={6}>6个月</option>
                  <option value={12}>12个月（1年）</option>
                  <option value={24}>24个月（2年）</option>
                  <option value={36}>36个月（3年）</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  生成数量 *
                </label>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={generateForm.count}
                  onChange={(e) => setGenerateForm({ ...generateForm, count: Number.parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">单次最多生成500个</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  备注
                </label>
                <textarea
                  value={generateForm.remark}
                  onChange={(e) => setGenerateForm({ ...generateForm, remark: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                取消
              </button>
              <button
                onClick={handleGenerate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                生成
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 导出弹窗 */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                导出授权码
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  状态筛选
                </label>
                <select
                  value={exportParams.status}
                  onChange={(e) => setExportParams({ ...exportParams, status: e.target.value as VipCodeStatus | '' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">全部状态</option>
                  <option value="unused">仅未使用</option>
                  <option value="used">仅已使用</option>
                  <option value="disabled">仅已禁用</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  VIP等级筛选
                </label>
                <select
                  value={exportParams.vipLevel}
                  onChange={(e) => setExportParams({ ...exportParams, vipLevel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">全部等级</option>
                  <option value="1">仅VIP</option>
                  <option value="2">仅VVIP</option>
                </select>
              </div>
              <p className="text-sm text-gray-500">
                导出格式：Excel (.xlsx)
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                取消
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                导出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批次管理弹窗 */}
      {showBatchesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                批次管理
              </h2>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {batches.length === 0 ? (
                <p className="text-gray-500 text-center py-8">暂无批次数据</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">名称</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">等级</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">有效期</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">总数/未用</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">创建时间</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {batches.map((batch) => {
                      const levelInfo = getVipLevelDisplay(batch.vip_level);
                      return (
                        <tr key={batch.batch_id}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {batch.name}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${levelInfo.bgColor} ${levelInfo.textColor}`}>
                              {levelInfo.name}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {batch.duration_months}个月
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="text-gray-900 dark:text-white">{batch.count}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-green-600">{batch.unused_count}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {formatDateTime(batch.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                setQueryParams({
                                  ...queryParams,
                                  batchId: batch.batch_id,
                                  page: 1,
                                });
                                setShowBatchesModal(false);
                              }}
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              查看
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowBatchesModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
